import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/img";
import { DEFAULT_PRODUCTS } from "@/config/site.config";

export default function Products() {
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);

  useEffect(() => {
    // Merge admin-managed products with the DEFAULT_PRODUCTS catalog so an
    // admin-added product augments — never wipes — the visible catalog.
    api
      .get("/products")
      .then(({ data }) => {
        const fromApi = (data || []).filter((p) => p.active !== false);
        const byId = new Map(DEFAULT_PRODUCTS.map((p) => [p.id, p]));
        fromApi.forEach((p) => byId.set(p.id, p));
        setProducts(Array.from(byId.values()).sort((a, b) => (a.order || 99) - (b.order || 99)));
      })
      .catch(() => {});
  }, []);

  return (
    <section id="products" className="section" data-testid="products-section">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="eyebrow mb-4">Products</div>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight max-w-3xl leading-[1.05]">
              A portfolio of <span className="gold-text italic">AI-native</span> products.
            </h2>
          </div>
          <p className="max-w-md text-white/55 text-sm leading-relaxed">
            Each product is a category leader in its field — sold separately or unified as a suite.
            The catalogue grows continuously; new modules can be added by AIBoxHub admins without a redeploy.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((p) => (
            <Link
              to={p.link || "/register"}
              key={p.id}
              className="group relative overflow-hidden rounded-2xl border border-white/8 bg-ink-200/60 hover:border-gold-500/35 transition-all"
              data-testid={`product-card-${p.id}`}
            >
              {p.image && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={resolveImg(p.image)}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                  <div className="absolute inset-x-0 top-0 h-[60%] bg-gradient-to-b from-transparent to-black/70 pointer-events-none" style={{ marginTop: "0" }} />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-300">{p.tag}</span>
                  <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-gold-300 transition-colors" />
                </div>
                <div className="mt-3 font-serif text-2xl text-white leading-tight">{p.name}</div>
                <p className="mt-1.5 text-sm text-white/55 leading-relaxed min-h-[44px]">
                  {p.description}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  {typeof p.price_from === "number" && p.price_from > 0 ? (
                    <span className="text-xs text-white/45 font-mono">
                      From <span className="gold-text font-serif text-base">${p.price_from}</span>/mo
                    </span>
                  ) : <span />}
                  <span className="inline-flex items-center gap-1 text-sm text-gold-300 group-hover:text-gold-200">
                    {p.cta || "Explore"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
              <div className="pointer-events-none absolute -bottom-12 -right-12 h-32 w-32 rounded-full bg-gold-500/0 group-hover:bg-gold-500/10 blur-2xl transition-all duration-500" />
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/products" className="pill pill-ghost text-sm" data-testid="products-view-all">
            View full marketplace
          </Link>
        </div>
      </div>
    </section>
  );
}
